/**
 * View component for /admin/authoring/studies/:studyId/roles
 *
 * Allows creation and update of new StudyRoles resource.
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import third-party libraries
import { DateTime } from 'luxon';
import ReactTooltip from 'react-tooltip'

// import actions
import * as studyActions from '../../study/studyActions';
import * as courseActions from '../../course/courseActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';
import * as studyRoleActions from '../studyRoleActions';
import * as studyUserActions from '../../studyUser/studyUserActions'; 

// import global components
import AlertModal from '../../../global/components/modals/AlertModal.js.jsx';
import Modal from '../../../global/components/modals/Modal.js.jsx';
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from '../../../global/components/BaseComponent.js.jsx';
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';
import { TextInput } from '../../../global/components/forms';

// import utils
import certUtils from "../../../global/utils/certUtils.js";

class AdminStudyRolesMatrix extends Base {
  constructor(props) {
    super(props);
    this.state = {
      addRoleModalOpen: false
      , confirmAddExcludedTmModalOpen: false
      , confirmRemoveExcludedTmModalOpen: false
      , confirmDeleteRoleModalOpen: false
      , exclusionNotAllowedModalOpen: false
      , newRoleString: ''
      , selectedStudyRole: null
      , selectedTmIds: null
      // hoveredRole allows us to highlight the whole row when a tm is hovered.
      , hoveredRole: null
      // currentRoleId is set when the user clicks a role title. It allows us to hide the rest of the roles.
      , currentRoleId: null
    }
    this._bind(
      '_checkAndSetExclusion'
      , '_createStudyRole'
      , '_deleteStudyRole'
      , '_exportData'
      , '_handleChange'
      , '_handleAddExcludedModulesToRole'
      , '_handleRemoveExcludedModulesFromRole'
      , '_handleAddOptionalModulesToRole'
      , '_handleRemoveOptionalModulesFromRole'
      , '_handleSetAllExcludedForRole'
      , '_handleSetAllOptionalForRole'
      , '_handleSetAllRequiredForRole'
      , '_isEmptyRole'
      , '_removeStudyRole'
    )
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(studyActions.fetchSingleIfNeeded(match.params.studyId));
    dispatch(courseActions.fetchListIfNeeded('_group', match.params.studyId));
    dispatch(contentModuleActions.fetchListIfNeeded('study', match.params.studyId));
    dispatch(studyRoleActions.fetchListIfNeeded('_group', match.params.studyId));
    dispatch(studyUserActions.fetchListIfNeeded('_group', match.params.studyId)); 
  }

  // delete a study role in the "draft" phase of a group (trash can button)
  _deleteStudyRole() {
    // console.log("delete study role", studyRoleId)
    const { dispatch, studyStore } = this.props;
    const { selectedStudyRole } = this.state;
    const study = studyStore.selected.getItem();
    if(selectedStudyRole && selectedStudyRole._id && study && study.status == 'draft') {
      // console.log("ok do it")
      dispatch(studyRoleActions.sendDelete(selectedStudyRole._id)).then(result => {
        dispatch(studyRoleActions.invalidateList('_group', study._id));
        dispatch(studyRoleActions.fetchListIfNeeded('_group', study._id));
        this.setState({
          selectedStudyRole: null
          , confirmDeleteRoleModalOpen: false
        });
      });
    } else {
      alert("WARNING: You can only delete a Role while a Program is still a draft. Once published they cannot be removed.")
      this.setState({
        selectedStudyRole: null
        , confirmDeleteRoleModalOpen: false
      });
    }
  }

  // delete roles that are not assigned to group users in a published group (separate button on the right hand side) 
  _removeStudyRole() {
    const { dispatch, studyStore, studyRoleStore } = this.props;
    const { selectedStudyRole } = this.state;
    const study = studyStore.selected.getItem();
    const studyRoles = studyRoleStore.util.getList('_group', study._id); 
    if(selectedStudyRole && selectedStudyRole._id && study && studyRoles.length > 1) {
      dispatch(studyRoleActions.sendRemoveStudyRole(study._id, selectedStudyRole._id)).then(result => {
        dispatch(studyRoleActions.invalidateList('_group', study._id));
        dispatch(studyRoleActions.fetchListIfNeeded('_group', study._id));
        this.setState({
          selectedStudyRole: null
          , confirmDeleteRoleModalOpen: false
        });
      });
    } else {
      alert("WARNING: A program must have at least ONE role associated with it.")
      this.setState({
        selectedStudyRole: null
        , confirmDeleteRoleModalOpen: false
      });
    }
  }

  _createStudyRole() {
    // console.log("create new study role")
    const { dispatch, studyStore } = this.props;
    const study = studyStore.selected.getItem();
    const { newRoleString } = this.state
    if(newRoleString && newRoleString.length > 0) {
      // console.log("ROLE", newRoleString)
      dispatch(studyRoleActions.sendCreateStudyRole({
        _group: study._id
        , _organization: study._organization
        , role: newRoleString
      })).then(result => {
        this.setState({
          newRoleString: ''
          , addRoleModalOpen: false
        })
        if(result.success) {
          dispatch(studyRoleActions.addStudyRoleToList(result.item._id, '_group', study._id));
        } else {
          // console.log('error', {result})
          alert("Something went wrong. Please try again.")
        }
      })
    }
  }

  _handleChange(e) {
    let newState = _.update(_.cloneDeep(this.state), e.target.name, () => {
      return e.target.value;
    });
    this.setState(newState);
  }

  // this takes an array of cmIds so we can easily add ability make a whole ROW optional at once.
  // TODO: Figure out how to make a whole COLUMN optional at once.
  _handleAddOptionalModulesToRole(studyRole, cmIds) {
    const { dispatch } = this.props;
    let newStudyRole = _.cloneDeep(studyRole);
    // add all cmIds to the _optionalModules array. Use Set to de-dupe
    newStudyRole._optionalModules = Array.from(new Set(newStudyRole._optionalModules.concat(cmIds)));
    // optional modules can't also be excluded, remove these cmIds from the excludedModules array
    newStudyRole._excludedModules = newStudyRole._excludedModules.filter(moduleId => !cmIds.includes(moduleId));
    dispatch(studyRoleActions.sendUpdateStudyRole(newStudyRole)).then(srRes => {
      this.setState({
        confirmSetAllOptionalModalOpen: false
      })
    })
  }

  _handleRemoveOptionalModulesFromRole(studyRole, cmIds) {
    const { dispatch } = this.props;
    let newStudyRole = _.cloneDeep(studyRole);
    // filter out any cmIds that were passed in.
    newStudyRole._optionalModules = newStudyRole._optionalModules.filter(moduleId => !cmIds.includes(moduleId));
    dispatch(studyRoleActions.sendUpdateStudyRole(newStudyRole)).then(srRes => {
      // console.log('srRes', srRes);
    });
  }

  _checkAndSetExclusion(type, studyRole, cmIds) {
    const { studyStore } = this.props;
    const study = studyStore.selected.getItem();
    this.setState({
      selectedStudyRole: studyRole
      , selectedTmIds: cmIds
    }, () => {
      // If study.status == 'draft' we don't need to make the user confirm.
      if(study.status == 'draft') {
        if(type == 'add') {
          this._handleAddExcludedModulesToRole()
        } else if(type == 'remove') {
          this._handleRemoveExcludedModulesFromRole();
        }
      } else {
        if(type == 'add') {
          this.setState({ confirmAddExcludedTmModalOpen: true })
        } else if(type == 'remove') {
          this.setState({ confirmRemoveExcludedTmModalOpen: true })
        }
      }
    });
  }


  _handleAddExcludedModulesToRole() {
    const { dispatch, contentModuleStore, studyStore } = this.props;
    const { selectedStudyRole, selectedTmIds } = this.state;
    const selectedStudy = studyStore.selected.getItem();
    /**
     * 1.12
     * Exclusions are only allowed on tms where tm._organization == study._organization
     * 
     * Check that before allowing the exclusion to be added.
     */

    const newStudyRole = _.cloneDeep(selectedStudyRole);
    // filter out contentModules that aren't tied to this study's sponsor
    const excludedModuleIds = selectedTmIds.filter(cmId => {
      const contentModule = contentModuleStore.byId[cmId];
      return (
        contentModule && contentModule._organization
        && selectedStudy && selectedStudy._organization
        && contentModule._organization === selectedStudy._organization
      )
    })
    // add all excludedModuleIds to the _excludedModules array. Use Set to de-dupe.
    newStudyRole._excludedModules = Array.from(new Set(newStudyRole._excludedModules.concat(excludedModuleIds)));

    // excluded modules can't also be optional, remove these cmIds from the optionalModules array
    newStudyRole._optionalModules = newStudyRole._optionalModules.filter(moduleId => !excludedModuleIds.includes(moduleId));
    dispatch(studyRoleActions.sendUpdateStudyRole(newStudyRole)).then(srRes => {
      // console.log('srRes', srRes);
      this.setState({
        confirmAddExcludedTmModalOpen: false
        , confirmSetAllExcludedModalOpen: false
        , selectedStudyRole: null
        , selectedTmIds: null
      });
    })
  }


  _handleRemoveExcludedModulesFromRole() {
    const { dispatch } = this.props;
    const { selectedStudyRole, selectedTmIds } = this.state;
    // console.log("including tm for role")
    let newStudyRole = _.cloneDeep(selectedStudyRole);
    newStudyRole._excludedModules = newStudyRole._excludedModules.filter(moduleId => moduleId != selectedTmIds)
    dispatch(studyRoleActions.sendUpdateStudyRole(newStudyRole)).then(srRes => {
      // console.log('srRes', srRes);
      this.setState({
        confirmRemoveExcludedTmModalOpen: false
        , selectedStudyRole: null
        , selectedTmIds: null
      });
    });
  }

  _handleSetAllExcludedForRole() {
    const { currentRoleId } = this.state;
    const { match, studyRoleStore, contentModuleStore } = this.props;
    const allContentModules = contentModuleStore.util.getList('study', match.params.studyId);
    const selectedStudyRole = studyRoleStore.byId[currentRoleId];
    this.setState({
      selectedStudyRole: selectedStudyRole
      , selectedTmIds: allContentModules.map(tm => tm._id)
    }, () => {
      this._handleAddExcludedModulesToRole()
    })
  }


  _handleSetAllRequiredForRole() {
    const { currentRoleId } = this.state;
    const { dispatch, studyRoleStore } = this.props;
    const newStudyRole = _.cloneDeep(studyRoleStore.byId[currentRoleId]);
    // making a module required just means removing it from both arrays. To set all required we just need to empty the arrays.
    newStudyRole._excludedModules = [];
    newStudyRole._optionalModules = [];
    dispatch(studyRoleActions.sendUpdateStudyRole(newStudyRole)).then(srRes => {
      this.setState({
        confirmSetAllRequiredModalOpen: false
      })
    });
  }

  _handleSetAllOptionalForRole() {
    const { currentRoleId } = this.state;
    const { match, studyRoleStore, contentModuleStore } = this.props;
    const allContentModules = contentModuleStore.util.getList('study', match.params.studyId);
    const selectedStudyRole = studyRoleStore.byId[currentRoleId];
    // send all content modules to the existing method.
    this._handleAddOptionalModulesToRole(selectedStudyRole, allContentModules.map(cm => cm._id))
  }

  _exportData(
    courses
    , studyRoles
  ) {
    const { studyStore, contentModuleStore } = this.props;
    const selectedStudy = studyStore.selected.getItem();
    let csv = certUtils.generateRoleBasedExclusionReport(
      courses
      , studyRoles
      , contentModuleStore.byId
    )
    const filename = `${selectedStudy.name}_ROLE_BASED_EXCLUSIONS_${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}.csv`
    // download as a file when the user clicks the button
    csv = 'data:text/csv;charset=utf-8,' + csv;
    // console.log(csv) // prints just fine
    let data = encodeURI(csv);
    // console.log(data) // breaks with special characters
    let link = document.createElement('a');
    link.setAttribute('href', data);
    link.setAttribute('download', filename);
    link.click();
  }

  _isEmptyRole(roleId) {
    const { studyStore, studyUserStore } = this.props; 
    const selectedStudy = studyStore.selected.getItem();
    const studyUsers = studyUserStore.util.getList('_group', selectedStudy._id);
    if(selectedStudy && selectedStudy.status == 'published' && studyUsers && !studyUsers.some(studyUser => studyUser._groupRole === roleId)) {
      return true; 
    } else {
      return false; 
    }
  }

  render() {
    const {
      courseStore
      , location
      , match
      , studyStore
      , studyRoleStore
      , studyUserStore
      , contentModuleStore

    } = this.props;

    const { currentRoleId } = this.state;
    /**
     * use the selected.getItem() utility to pull the actual study object from the map
     */
    const study = studyStore.selected.getItem();
    const courses = courseStore.util.getList('_group', match.params.studyId);
    const contentModules = contentModuleStore.util.getList('study', match.params.studyId);
    const studyRoles = studyRoleStore.util.getList('_group', match.params.studyId);
    const studyUsers = studyUserStore.util.getList('_group', match.params.studyId); 

    // sort alphabetically by studyRole.role
    const sortedStudyRoles = studyRoles ? studyRoles.sort((a, b) => a.role > b.role ? 1 : -1) : [];

    // for better UI. need to know how many (if any) are allowed to be set excluded
    const excludableModules = study && study._organization && contentModules && contentModules.length ? contentModules.filter(tm => study._organization === tm._organization) : [];

    const isEmpty = (
      !study
      || !study._id
      || studyStore.selected.didInvalidate
      || !courses
      || !contentModules
      || !studyRoles
    );
    
    // makes all of the "set all" stuff easier because it's only allowed when we have a currentRoleId
    const currentStudyRole = !isEmpty && currentRoleId ? studyRoleStore.byId[currentRoleId] : null;

    return (
    <AuthoringLayout>
      <div className="flex">
        <section className="section">
          <div className="yt-container">
            <Breadcrumbs links={location.state.breadcrumbs} />
            { isEmpty ?
              (studyStore.selected.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
              : 
              <div style={{ opacity: studyStore.selected.isFetching ? 0.5 : 1 }}>
                <div className="yt-row with-gutters">
                  {/* main table section */}
                  <div className="yt-col full" style={{ maxWidth: "100%" }}>
                    <div className="content-container study-roles-matrix" >
                      <div className="-matrix-header">
                        <div className="yt-row">
                          <strong>Manage Training Roles for {study.name}</strong>
                        </div>
                        <button type="button" className="yt-btn small danger"
                          onClick={ () => this._exportData(
                            courses
                            , sortedStudyRoles
                          )}
                        >
                          Export
                        </button>
                        <div className="yt-row right">
                          <div className="-matrix-legend">
                            <span className="-key"><i className="fas fa-minus-square -excluded -disabled" /> EXCLUDED</span>
                            <span className="-key"><i className="fas fa-asterisk -required -disabled" />REQUIRED</span>
                            <span className="-key"><i className="far fa-circle -optional -disabled" />OPTIONAL</span>
                          </div>
                        </div>
                      </div>
                      {/* 
                        NOTES: the goal here is the big role/module matrix,
                        first a set of headers kind of like the analytics page in how they line up
                        ex:
                        | ----- Course A ----- | ----- Course B ----- |
                        | - TMA1 - | - TMA2 -  | - TMA1 - | - TMA2 -  |

                        then underneath, and lining up with the courses/modules,

                        Role 1 |    -     |     X    |      X    |      -     |
                        Role 2 |    -     |     X    |      X    |      -     |
                        
                        and so on down the page. X's are the already excluded ones, 
                        and they can click the empty spaces to create new exclusions
                        LOTS of warnings and modals about not making changes
                        - this will be left completely alone for the majority of studies
                      */}
                      <table>
                        {/* first row */}
                        <thead>
                          <tr>
                            {/* This is only here to help line everything up. It is supposed to match the height of the course title rows. */}
                            <th />
                            {/* now course titles */}
                            { courses.map((course, i) =>
                              <th className="-course-column" style={{backgroundColor: "#fff"}} key={course._id + "_" + i} colSpan={`${course._contentModules.length + 1}`}>
                                <span className="-course-title" >
                                {course.title}
                                </span>
                              </th>
                            )}
                          
                          </tr>
                          {/* second row */}
                          <tr>
                            <th className="-tm-col">
                              <span className="-tm-title">
                                <b>Role</b>
                              </span>
                            </th>
                            {/* now tm titles */}
                            { courses.map((course, i) =>
                              course._contentModules.map((cmId, i) =>
                                <th
                                  className="-tm-col" key={cmId + "_" + i}
                                  onMouseEnter={() => this.setState({ hoveredTm: cmId })}
                                  style={i === 0 ? {borderLeft: '1px solid lightgray'} : {}}
                                >
                                  <p className="-tm-title" data-tip data-for={`tm-info_${cmId}`}>
                                    <ReactTooltip
                                      aria-haspopup="true"
                                      className="yt-tooltip"
                                      id={`tm-info_${cmId}`}
                                      place="top"
                                      type="light"
                                    >
                                      <span>{contentModuleStore.byId[cmId] ? contentModuleStore.byId[cmId].title : "loading"}</span>
                                    </ReactTooltip>
                                    <em>{contentModuleStore.byId[cmId] ? contentModuleStore.byId[cmId].title : "loading"}</em>
                                  </p>
                                </th>
                            ))}
                            <th className="-tm-col">
                              <span className="-tm-title">

                              </span>
                            </th>
                          </tr>
                        </thead>
                        {/* third row */}
                        {/* role name then each tm for that role */}
                        <tbody>
                        { sortedStudyRoles.map((studyRole, i) =>
                          <tr
                            className={`-role-row ${this.state.hoveredRole == studyRole._id ? '-highlighted' : ''} ${this.state.currentRoleId && this.state.currentRoleId != studyRole._id ? '-collapsed' : ''}`}
                            onMouseEnter={() => this.setState({ hoveredRole: studyRole._id })}
                            key={studyRole._id + "_row_" + i}
                          >
                            <th className={`-study-role-cell `}>
                              <span className="-study-role">
                                { this.state.currentRoleId && this.state.currentRoleId == studyRole._id ?
                                  // This is the current role, meaning it is displayed by itself. Give it an icon to show the user that they can click to see the whole list of roles again.
                                  <i className="fa fa-window-close action-link" onClick={() => this.setState({ currentRoleId: null })} />
                                  :
                                  study.status == "draft" ?
                                  <i className="fa fa-trash action-link" onClick={() => this.setState({ confirmDeleteRoleModalOpen: true, selectedStudyRole: studyRole })}></i>
                                  :
                                  null
                                }
                                <em className="" data-tip data-for={`sr-info_${studyRole._id}`} onClick={() => this.setState({ currentRoleId: studyRole._id })} >                                  
                                  {studyRole.role}
                                  <ReactTooltip
                                    aria-haspopup="true"
                                    className="yt-tooltip"
                                    id={`sr-info_${studyRole._id}`}
                                    place="top"
                                    type="light"
                                  >
                                    <span>{studyRole.role}</span>
                                  </ReactTooltip>
                                </em>
                              </span>
                            </th>

                            {/* Now tms for this studyRole */}
                            {courses.map((course, i) =>
                              course._contentModules.map((cmId, i) =>
                              <td
                                className={`-role-tm-toggle ${(this.state.hoveredRole == studyRole._id || this.state.hoveredTm == cmId) ? '-highlighted' : ''} ${this.state.currentRoleId && this.state.currentRoleId != studyRole._id ? '-collapsed' : ''}`}
                                key={studyRole._id + "_" + i}
                                onMouseEnter={() => this.setState({hoveredTm: cmId})}
                              >
                                <span style={{whiteSpace: 'nowrap'}}>
                                  {studyRole._excludedModules.includes(cmId) ?
                                    // module is excluded
                                    <span style={{ whiteSpace: 'nowrap' }}>
                                      <i className="fas fa-minus-square -excluded" onClick={() => this._checkAndSetExclusion("remove", studyRole, [cmId])} />
                                      <i className="fas fa-asterisk -required -inactive -disabled" />
                                      <i className="far fa-circle -optional -inactive -disabled" />
                                    </span>
                                    :
                                    <span style={{ whiteSpace: 'nowrap' }}>
                                      {contentModuleStore.byId[cmId] && contentModuleStore.byId[cmId]._organization && contentModuleStore.byId[cmId]._organization == study._organization ?
                                        // module is NOT excluded, but can be set excluded
                                        <i className="fas fa-minus-square -excluded -inactive" onClick={() => this._checkAndSetExclusion("add", studyRole, [cmId])} />
                                        :
                                        // module not allowed to be set excluded
                                        <i className="fas fa-ban -inactive -disabled" onClick={() => this.setState({ exclusionNotAllowedModalOpen: true })} />
                                      }
                                      {studyRole._optionalModules.includes(cmId) ?
                                        // module is optional
                                        <span style={{ whiteSpace: 'nowrap' }}>
                                          <i className="fas fa-asterisk -required -inactive" onClick={() => this._handleRemoveOptionalModulesFromRole(studyRole, [cmId])} />
                                          <i className="far fa-circle -optional -disabled" />
                                        </span>
                                        :
                                        // module is required
                                        <span style={{ whiteSpace: 'nowrap' }}>
                                          <i className="fas fa-asterisk -required -disabled" />
                                          <i className="far fa-circle -optional -inactive" onClick={() => this._handleAddOptionalModulesToRole(studyRole, [cmId])} />
                                        </span>
                                      }
                                    </span>
                                  }
                                </span>
                              </td>
                            ))}
                            { this._isEmptyRole(studyRole._id) ?
                              <div style={{backgroundColor: '#fff', height: '100%', padding: '10px'}}>
                                <button className="yt-btn x-small link danger" onClick={() => this.setState({ confirmDeleteRoleModalOpen: true, selectedStudyRole: studyRole })}>
                                  <p style={{textColor: 'red'}}></p>Remove
                                </button>
                              </div>
                            : null
                            }
                          </tr>
                          )}
                        </tbody>
                      </table>
                      { !currentStudyRole || study.status === "archived" ? null // archived studies shouldn't allow changes
                        : // a single studyRole is selected. Show the controls for setting all by studyRole
                        <div className="yt-row" style={{position: 'sticky', left: '0'}}>
                          <div className="yt-col full">
                            <strong>{currentStudyRole.role}</strong>
                            {/* give the user some info to help the understand what's going on */}
                            <p>{`${currentStudyRole._excludedModules.length} excluded`}</p>
                            <p>{`${currentStudyRole._optionalModules.length} optional`}</p>
                          </div>
                          <div className="yt-col full">
                            {excludableModules.length > currentStudyRole._excludedModules.length ?
                              // at least one excludeable module is not yet excluded, show the button
                              <p><button className="yt-btn danger small" type="button" onClick={() => this.setState({ confirmSetAllExcludedModalOpen: true })}>Set All Excluded For This Role</button></p>
                              :
                              null
                            }
                            {currentStudyRole._excludedModules.length + currentStudyRole._optionalModules.length > 0 ?
                              // at least one of their modules is not required, show the button
                              <p><button className="yt-btn warning small" type="button" onClick={() => this.setState({ confirmSetAllRequiredModalOpen: true })}>Set All Required For This Role</button></p>
                              :
                              null
                            }
                            {contentModules.length > currentStudyRole._optionalModules.length ?
                              // at least one of their modules is not optional, show the button
                              <p><button className="yt-btn info small" type="button" onClick={() => this.setState({ confirmSetAllOptionalModalOpen: true })}>Set All Optional For This Role</button></p>
                              :
                              null
                            }
                          </div>
                          <hr/>
                        </div>
                      }
                      <div className="yt-row" style={{position: 'sticky', left: '0'}}>
                        <button className="yt-btn success small" type="button" onClick={() => this.setState({ addRoleModalOpen: true })}>Add New Role</button>
                      </div>
                    </div>
                  </div>
                  {/* Sidebar info and nav */}
                  <div className="yt-col full">
                    <div className="content-container">
                      <p><strong>Status</strong></p>
                      { study.status == "draft" ? 
                        <p>This Program is currently a <b>DRAFT</b>. You can freely add/remove roles and their exclusions.</p>
                      : study.status == "published" ? 
                        <p>This program is currently <b>PUBLISHED</b>. Many changes to the program structure will be restricted at this time. You can add new roles but cannot edit or remove existing ones.</p>
                      : study.status == "archived" ?
                        <p>This Program is currently <b>ARCHIVED</b>. You cannot make changes.</p>
                      :
                        null
                      }
                      {/* <br/>
                      <p><strong>Instructions</strong></p>
                      <p>More notes here describing what is going on and why</p> */}
                    </div>
                  </div>
                </div>
                <AlertModal
                  alertMessage={<div>Are you sure you want to add this exclusion to this role? Any users with existing certificates for this module will be replaced.</div> }
                  alertTitle="Add exclusion"
                  closeAction={() => this.setState({ confirmAddExcludedTmModalOpen: false, selectedStudyRole: null, selectedTmIds: [] })}
                  declineText="Never mind"
                  confirmAction={this._handleAddExcludedModulesToRole}
                  confirmText="Yes"
                  isOpen={this.state.confirmAddExcludedTmModalOpen}
                  type="warning"
                />
                <AlertModal
                  alertMessage={<div>Are you sure you want to *remove* this exclusion from this role? Any users who currently rely on this exclusion will have them invalidated.</div> }
                  alertTitle="Remove exclusion"
                  closeAction={() => this.setState({ confirmRemoveExcludedTmModalOpen: false, selectedStudyRole: null, selectedTmIds: null })}
                  declineText="Never mind"
                  confirmAction={this._handleRemoveExcludedModulesFromRole}
                  confirmText="Yes"
                  isOpen={this.state.confirmRemoveExcludedTmModalOpen}
                  type="warning"
                />
                <AlertModal
                  alertMessage={<div>This content module is not tied to a specific sponsor and therefore cannot be excluded.</div>}
                  alertTitle="Cannot exclude shared content module"
                  closeAction={() => this.setState({ exclusionNotAllowedModalOpen: false })}
                  declineText="Okay"
                  isOpen={this.state.exclusionNotAllowedModalOpen}
                  type="info"
                />
                {currentStudyRole && study.status !== "archived" ? // archived studies shouldn't allow changes.
                  <div>
                    <AlertModal
                      alertTitle={`Set all excluded for ${currentStudyRole.role}`}
                      alertMessage={
                        // <div>Are you sure you want to exclude all modules for this role?<br /> <strong>NOTE: </strong> Only the modules that are specific to this study's sponsor will be set as excluded -- {excludableModules.length} module(s)</div>}
                        <div>
                          Are you sure you want to exclude all modules for this role?
                          <br />
                          <strong>NOTE: </strong> Only the modules that are specific to this study's sponsor will be set as excluded -- {excludableModules.length} module(s)
                          <br/>
                          { study.status === "published" ? // only add the warning if the study is published.
                            <p><strong>WARNING: </strong> Any users with existing certificates for these modules will be replaced.</p>
                            :
                            null
                          }
                        </div>
                      }
                      closeAction={() => this.setState({ confirmSetAllExcludedModalOpen: false })}
                      declineText="Never mind"
                      confirmAction={this._handleSetAllExcludedForRole}
                      confirmText="Yes"
                      isOpen={this.state.confirmSetAllExcludedModalOpen}
                      type="danger"
                    />
                    <AlertModal
                      alertMessage={
                        <div>
                          Are you sure you want to require all modules for this role?
                          <br />
                          {currentStudyRole._excludedModules.length && study.status === "published" ? // only add the warning if the study is published.
                            <p><strong>WARNING: </strong> This will *remove* {currentStudyRole._excludedModules.length} exclusion(s) for this role. Any users who currently rely on these exclusions will have them invalidated.</p>
                            :
                            null
                          }
                        </div>
                      }
                      alertTitle={`Set all required for ${currentStudyRole.role}`}
                      closeAction={() => this.setState({ confirmSetAllRequiredModalOpen: false })}
                      declineText="Never mind"
                      confirmAction={this._handleSetAllRequiredForRole}
                      confirmText="Yes"
                      isOpen={this.state.confirmSetAllRequiredModalOpen}
                      type="warning"
                    />
                    <AlertModal
                      alertMessage={
                        <div>
                          Are you sure you want to set all modules optional for this role?
                          <br />
                          { currentStudyRole._excludedModules.length && study.status === "published" ? //  only add the warning if the study is published
                            <p><strong>WARNING: </strong> This will *remove* {currentStudyRole._excludedModules.length} exclusion(s) for this role. Any users who currently rely on these exclusions will have them invalidated.</p>
                            :
                            null
                          }
                        </div>
                      }
                      alertTitle={`Set all optional for ${currentStudyRole.role}`}
                      closeAction={() => this.setState({ confirmSetAllOptionalModalOpen: false })}
                      declineText="Never mind"
                      confirmAction={this._handleSetAllOptionalForRole}
                      confirmText="Yes"
                      isOpen={this.state.confirmSetAllOptionalModalOpen}
                      type="info"
                    />
                  </div>
                  :
                  null
                }
                <Modal
                  btnColor="info"
                  closeAction={() => this.setState({newRoleString: "", addRoleModalOpen: false})}
                  closeText="Cancel"
                  confirmAction={() => this._createStudyRole()}
                  confirmText="Create Role"
                  isOpen={this.state.addRoleModalOpen}
                  modalClasses="info"
                  modalHeader={<span>Create new role</span>}
                >
                  <div className="form-container">
                    <span>
                      To create a new Role for this Program, type its name below. (No spaces, all lowercase, ex: study-person). WARNING if the Program is already published, this cannot be undone.
                    </span>
                    <TextInput
                      value={this.state.newRoleString}
                      name="newRoleString"
                      change={this._handleChange}
                      placeholder="ex: my-new-role"
                      />
                  </div>
                </Modal>
                <AlertModal
                  alertMessage={"Are you sure you want to remove this Role from the Program?"}
                  alertTitle="Delete role"
                  closeAction={() => this.setState({confirmDeleteRoleModalOpen: false, selectedStudyRole: null})}
                  declineText="Never mind"
                  confirmAction={study.status == 'draft' ? this._deleteStudyRole : this._removeStudyRole}
                  confirmText={"Delete Role"}
                  isOpen={this.state.confirmDeleteRoleModalOpen}
                  type="warning"
                />
              </div>
              }
          </div>
        </section>
      </div>
    </AuthoringLayout>
    )
  }
}

AdminStudyRolesMatrix.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  return {
    courseStore: store.course
    , studyStore: store.study
    , studyRoleStore: store.studyRole
    , studyUserStore: store.studyUser
    , contentModuleStore: store.contentModule
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminStudyRolesMatrix)
);





// WEBPACK FOOTER //
// modules/studyRole/views/AdminStudyRolesMatrix.js.jsx